import { Icon } from "@iconify/react";
import { motion, useReducedMotion } from "motion/react";
import logo from "@/assets/images/elevintia-logo.svg";
import { useHardwareAcceleration } from "@/hooks/use-hardware-acceleration";
import RevealText from "../ui/reveal-text";
import CopyEmailButton, { CONTACT_EMAIL } from "../ui/copy-email-button";

const footerLinks = [
  {
    label: "LinkedIn",
    value: "@elevintia",
    href: "https://www.linkedin.com/company/elevintia",
  },
  {
    label: "Telegram",
    value: "@elevintia",
    href: "https://t.me/elevintia",
  },
  {
    label: "X",
    value: "@elevintia",
    href: "https://x.com/elevintia",
  },
  {
    label: "YouTube",
    value: "@elevintia",
    href: "https://youtube.com/@elevintia",
  },
];

export default function DesktopContact() {
  const reduceMotion = useReducedMotion();
  const hasHardwareAcceleration = useHardwareAcceleration();
  const motionDisabled = Boolean(reduceMotion) || !hasHardwareAcceleration;

  return (
    <section
      id="contact"
      data-desktop-section="contact"
      className="h-dvh px-8 lg:px-12"
    >
      <div className="mx-auto flex h-full w-full max-w-6xl flex-col pb-6 pt-[clamp(7rem,15vh,9rem)] lg:pb-8">
        <motion.div
          initial={{
            opacity: 0,
            transform: motionDisabled
              ? "translate3d(0, 0, 0)"
              : "translate3d(0, 28px, 0)",
          }}
          whileInView={{
            opacity: 1,
            transform: "translate3d(0, 0, 0)",
          }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: motionDisabled ? 0 : 0.5 }}
          className="mt-auto pb-10"
        >
          <RevealText
            as="h2"
            trigger="inView"
            text="Let's build what's next."
            className="mb-10 text-[clamp(1.4rem,2.4vw,2rem)] font-light tracking-[-0.02em] text-white/70"
          />
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="group relative inline-flex max-w-full items-center gap-4 pb-3 text-[clamp(2.1rem,5vw,5rem)] font-light leading-none tracking-[-0.05em]"
          >
            <span className="break-all">{CONTACT_EMAIL}</span>
            <span
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-linear-to-r from-[#149CFF] to-[#DEFF77] transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100 group-focus-visible:scale-x-100"
            />
            <Icon
              icon="material-symbols:arrow-outward"
              className="shrink-0 text-[0.48em] text-white/45 transition-transform duration-200 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-white group-focus-visible:-translate-y-1 group-focus-visible:translate-x-1 group-focus-visible:text-white"
            />
          </a>
          <div className="mt-6">
            <CopyEmailButton />
          </div>
        </motion.div>

        <div>
          <nav
            aria-label="Contact and social links"
            className="grid grid-cols-2 border-y border-white/15"
          >
            {footerLinks.map((link, index) => (
              <motion.a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                initial={{
                  opacity: 0,
                  transform: motionDisabled
                    ? "translate3d(0, 0, 0)"
                    : "translate3d(0, 18px, 0)",
                }}
                whileInView={{
                  opacity: 1,
                  transform: "translate3d(0, 0, 0)",
                }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{
                  duration: motionDisabled ? 0 : 0.35,
                  delay: motionDisabled ? 0 : index * 0.05,
                }}
                className={`group flex items-center justify-between gap-4 py-5 transition-colors hover:bg-white/[0.035] focus-visible:bg-white/[0.035] focus-visible:outline-none lg:py-6 ${
                  index % 2 === 0
                    ? "border-r border-white/15 pr-5 lg:pr-8"
                    : "pl-5 lg:pl-8"
                } ${index < 2 ? "border-b border-white/15" : ""}`}
              >
                <span>
                  <span className="block text-xs text-white/42">
                    {link.label}
                  </span>
                  <span className="mt-1 block text-base font-light text-white/80 lg:text-lg">
                    {link.value}
                  </span>
                </span>
                <Icon
                  icon="material-symbols:arrow-outward"
                  className="text-lg text-white/35 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white"
                />
              </motion.a>
            ))}
          </nav>

          <footer className="mt-8 flex items-center justify-between text-xs text-white/40">
            <img
              src={logo}
              alt="Elevintia"
              className="h-7 select-none opacity-75"
              draggable={false}
            />
            <p>© 2026 Elevintia. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </section>
  );
}
